import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { withCsrfHeader } from "@/lib/queryClient";

// Only allow internal app paths as a returnTo target. Blocks open-redirects
// and protocol-relative (`//evil.com`) / absolute URLs. Accepts any path that
// starts with "/" but not "//" (protocol-relative) or anything with a host.
function getSafeReturnTo(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("returnTo");
  if (!raw) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  // Must be a relative path: starts with "/" but not "//" or "/<space>" etc.
  if (!decoded.startsWith("/") || decoded.startsWith("//") || /^\/[^/]/.test(decoded) === false) return null;
  // Reject anything that looks like it contains a host (e.g. "/http:", "/@")
  if (/^\/[a-z][a-z0-9+\-.]*:/i.test(decoded)) return null;
  // Reject returnTo that itself points back to /login — this prevents the
  // infinite /login?returnTo=/login?returnTo=... nesting loop that occurs
  // when a SESSION_EXPIRED 401 fires from the /login page itself.
  if (decoded === "/login" || decoded.startsWith("/login?") || decoded.startsWith("/login/")) return null;
  return decoded;
}

// Google sign-in failures come back as a redirect to /login?error=<code>
// (the callback is a browser navigation, so there is no response body to read).
const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  no_account:
    "There's no Clarik account for that Google address. Ask your workspace admin for an invite, or request access below.",
  account_deactivated:
    "This account has been deactivated. Contact your workspace admin.",
  google_email_unverified:
    "That Google address isn't verified with Google, so we can't sign you in with it.",
  google_cancelled: "Google sign-in was cancelled.",
  google_state_invalid:
    "That Google sign-in link expired. Please try again.",
  google_unavailable:
    "Google sign-in isn't available right now. Use your email and password.",
};

function getGoogleError(): string {
  if (typeof window === "undefined") return "";
  const code = new URLSearchParams(window.location.search).get("error");
  if (!code) return "";
  return GOOGLE_ERROR_MESSAGES[code] || "Google sign-in failed. Please try again.";
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(getGoogleError);
  const [loading, setLoading] = useState(false);

  // Task #253 — carry the originating workspace path so an OAuth round-trip or
  // deep-link login lands the user right back where they were.
  const returnTo = getSafeReturnTo();

  // ?fresh=1 is appended by /api/logout so a just-logged-out user always
  // sees the form rather than being auto-bounced back to their old session.
  const isFresh = new URLSearchParams(window.location.search).has("fresh");

  // If the session is actually still valid (a transient blip sent the user
  // here from a deep /w/<slug> URL), don't make them re-type credentials —
  // forward straight back to returnTo without ever showing the form.
  // Skip this when ?fresh=1 is present so a freshly-logged-out user can
  // type new credentials even if a stale/wrong session is still detectable.
  useEffect(() => {
    if (isFresh) return; // never auto-redirect after a deliberate logout
    let cancelled = false;
    fetch("/api/auth/user", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((user) => {
        if (cancelled || !user) return;
        // Do NOT reset the bounce counter here. Resetting it from the Login
        // page created an infinite loop: the counter would reach 1, Login
        // would see a momentarily-valid session and reset it to 0, then the
        // workspace would fail again, counter back to 1... forever. The
        // counter is now only reset from AuthenticatedApp (App.tsx) once the
        // workspace actually renders with a confirmed session. That way the
        // circuit breaker (max 3 bounces → ?fresh=1) can actually fire.
        window.location.replace(returnTo || "/");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [returnTo, isFresh]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/login/password", {
        method: "POST",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Login failed");
      }

      const data = await res.json();
      // Task #190 — first-login password reset flow.
      if (data.mustResetPassword) {
        window.location.href = "/first-login";
        return;
      }
      // Task #128 — multi-workspace handling.
      // requiresWorkspaceSelection: true → user has >1 workspace, must pick one.
      // workspaceSlug: null + no flag → user has 0 workspaces.
      if (data.requiresWorkspaceSelection) {
        window.location.href = "/select-workspace";
        return;
      }
      if (!data.workspaceSlug) {
        window.location.href = "/no-workspace-access";
        return;
      }
      // Task #253 — honor a safe workspace returnTo so OAuth round-trips and
      // deep-link logins land back on the originating page. A slug mismatch
      // (returnTo points at a different workspace) is corrected downstream by
      // WorkspaceRouteHandler's session-bound workspace check.
      window.location.href = returnTo || `/w/${data.workspaceSlug}/`;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>
            Sign in to your Clarik account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded text-sm">
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="you@example.com"
                required
                autoFocus
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="Enter your password"
                required
                data-testid="input-password"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="button-login"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Sign in with Google — identity only. The server matches the
                verified Google email onto an EXISTING Clarik user; it never
                creates an account (Clarik is invite-only). */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                const rt = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
                window.location.href = `/api/auth/google${rt}`;
              }}
              data-testid="button-login-google"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.85 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.67-2.84z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"
                />
              </svg>
              Sign in with Google
            </Button>

            <div className="text-center text-sm">
              <a href="/forgot-password" className="text-primary hover:underline" data-testid="link-forgot-password">
                Forgot password?
              </a>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <a href="/signup" className="text-primary hover:underline">
                Request access
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
